import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { MultiSelect } from '../ui/multi-select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Star, Trash2, UserPlus, AlertTriangle } from 'lucide-react';
import {
  GET_PROJECT,
  GET_PROJECT_ASSIGNABLE_EMPLOYEES,
  GET_PROJECT_TASKS,
  REMOVE_PROJECT_MEMBER_MUTATION,
  SET_PROJECT_MEMBERS_MUTATION,
} from '../../graphql/queries';
import { usePermission } from '../../hooks/usePermission';
import { compareEmployeesByLastName } from './projectUtils';

export interface ProjectMembersProject {
  id: string;
  leaderId?: string | null;
  leader?: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
  } | null;
  members?: Array<{
    id: string;
    employeeId: string;
    employee?: {
      id: string;
      firstName: string;
      lastName: string;
      email?: string;
    } | null;
  }> | null;
}

interface ProjectMembersTabProps {
  project: ProjectMembersProject;
  currentUserId?: string;
}

interface RemovalTarget {
  employeeId: string;
  fullName: string;
  taskCount: number;
}

export const ProjectMembersTab: React.FC<ProjectMembersTabProps> = ({
  project,
  currentUserId,
}) => {
  const canManageAll = usePermission('projects.manage_all');
  const canManageOwn = usePermission('projects.manage_own');
  const isLeader = !!currentUserId && project.leaderId === currentUserId;
  const canEdit = canManageAll || (canManageOwn && isLeader);

  const { data: employeesData, loading: employeesLoading } = useQuery<{
    projectAssignableEmployees: Array<{
      id: string;
      firstName: string;
      lastName: string;
      isActive: boolean;
    }>;
  }>(GET_PROJECT_ASSIGNABLE_EMPLOYEES, {
    variables: { projectId: project.id },
    fetchPolicy: 'cache-and-network',
    skip: !canEdit,
  });

  const { data: tasksData } = useQuery<{
    projectTasks: Array<{
      id: string;
      assignees?: Array<{ employeeId: string }> | null;
    }>;
  }>(GET_PROJECT_TASKS, {
    variables: { projectId: project.id },
    fetchPolicy: 'cache-and-network',
  });

  const [setProjectMembers, { loading: addingMember }] = useMutation(
    SET_PROJECT_MEMBERS_MUTATION,
    {
      refetchQueries: [{ query: GET_PROJECT, variables: { id: project.id } }],
      awaitRefetchQueries: true,
    },
  );

  const [removeProjectMember, { loading: removing }] = useMutation(
    REMOVE_PROJECT_MEMBER_MUTATION,
    {
      refetchQueries: [
        { query: GET_PROJECT, variables: { id: project.id } },
        { query: GET_PROJECT_TASKS, variables: { projectId: project.id } },
      ],
      awaitRefetchQueries: true,
    },
  );

  const memberEmployeeIds = useMemo(
    () =>
      (project.members || [])
        .map((m) => m.employeeId)
        .filter(Boolean) as string[],
    [project.members],
  );

  const [pendingAddIds, setPendingAddIds] = useState<string[]>([]);
  const [removalTarget, setRemovalTarget] = useState<RemovalTarget | null>(
    null,
  );
  const [reassignToId, setReassignToId] = useState<string>('');

  useEffect(() => {
    if (!removalTarget) {
      setReassignToId('');
    }
  }, [removalTarget]);

  const taskCountByEmployee = useMemo(() => {
    const map = new Map<string, number>();
    for (const task of tasksData?.projectTasks || []) {
      for (const a of task.assignees || []) {
        map.set(a.employeeId, (map.get(a.employeeId) ?? 0) + 1);
      }
    }
    return map;
  }, [tasksData]);

  const employeeById = useMemo(() => {
    const map = new Map<
      string,
      { id: string; firstName: string; lastName: string; isActive: boolean }
    >();
    for (const e of employeesData?.projectAssignableEmployees || []) {
      map.set(e.id, e);
    }
    return map;
  }, [employeesData]);

  const addableEmployees = useMemo(() => {
    const taken = new Set<string>(memberEmployeeIds);
    return (employeesData?.projectAssignableEmployees || [])
      .filter((e) => e.isActive && !taken.has(e.id))
      .sort(compareEmployeesByLastName);
  }, [employeesData, memberEmployeeIds]);

  const memberRows = useMemo(() => {
    const rows = (project.members || []).map((m) => {
      const employee = m.employee;
      const fullName = employee
        ? `${employee.firstName} ${employee.lastName}`
        : '—';
      return {
        memberId: m.id,
        employeeId: m.employeeId,
        firstName: employee?.firstName || '',
        lastName: employee?.lastName || '',
        fullName,
        email: employee?.email,
        isLeader: project.leaderId === m.employeeId,
        taskCount: taskCountByEmployee.get(m.employeeId) ?? 0,
      };
    });
    rows.sort((a, b) => {
      if (a.isLeader !== b.isLeader) return a.isLeader ? -1 : 1;
      return compareEmployeesByLastName(a, b);
    });
    return rows;
  }, [project.members, project.leaderId, taskCountByEmployee]);

  const reassignOptions = useMemo(() => {
    if (!removalTarget) return [];
    return memberRows
      .filter((row) => row.employeeId !== removalTarget.employeeId)
      .map((row) => {
        const pickerLabel = `${row.lastName} ${row.firstName}`;
        return {
          value: row.employeeId,
          label: row.isLeader ? `${pickerLabel} (lider)` : pickerLabel,
        };
      });
  }, [memberRows, removalTarget]);

  const handleAddMembers = async () => {
    if (pendingAddIds.length === 0) return;
    const next = Array.from(new Set([...memberEmployeeIds, ...pendingAddIds]));
    try {
      await setProjectMembers({
        variables: { input: { projectId: project.id, employeeIds: next } },
      });
      toast.success(
        pendingAddIds.length === 1
          ? (() => {
              const added = employeeById.get(pendingAddIds[0]);
              return added
                ? `Dodano: ${added.firstName} ${added.lastName}`
                : 'Członek dodany';
            })()
          : `Dodano ${pendingAddIds.length} członków`,
      );
      setPendingAddIds([]);
    } catch (err: any) {
      toast.error(err?.message || 'Nie udało się dodać członków');
    }
  };

  const openRemovalDialog = (
    employeeId: string,
    fullName: string,
    taskCount: number,
  ) => {
    setRemovalTarget({ employeeId, fullName, taskCount });
  };

  const handleConfirmRemove = async () => {
    if (!removalTarget) return;
    try {
      await removeProjectMember({
        variables: {
          input: {
            projectId: project.id,
            employeeId: removalTarget.employeeId,
            reassignToEmployeeId: reassignToId || undefined,
          },
        },
      });
      toast.success(
        reassignToId
          ? 'Członek usunięty, zadania przepisane'
          : 'Członek usunięty',
      );
      setRemovalTarget(null);
    } catch (err: any) {
      toast.error(err?.message || 'Nie udało się usunąć członka');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-500" />
            Lider projektu
          </CardTitle>
        </CardHeader>
        <CardContent>
          {project.leader ? (
            <Badge variant="outline" className="text-sm py-1 px-2">
              {project.leader.firstName} {project.leader.lastName}
              {project.leader.email && (
                <span className="text-muted-foreground ml-2 text-xs">
                  {project.leader.email}
                </span>
              )}
            </Badge>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              Brak przypisanego lidera
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            Lider projektu jest ustawiany w zakładce „Ustawienia”.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Członkowie projektu ({memberRows.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {memberRows.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              Brak członków projektu
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border">
              {memberRows.map((row) => (
                <li
                  key={row.memberId}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">
                        {row.fullName}
                      </span>
                      {row.isLeader && (
                        <Badge
                          variant="outline"
                          className="text-[10px] py-0 px-1.5 h-5 border-amber-300 bg-amber-50 text-amber-700"
                        >
                          <Star className="w-3 h-3 mr-0.5" />
                          Lider
                        </Badge>
                      )}
                      {row.taskCount > 0 && (
                        <Badge
                          variant="outline"
                          className="text-[10px] py-0 px-1.5 h-5"
                        >
                          {row.taskCount} {row.taskCount === 1 ? 'zadanie' : 'zadań'}
                        </Badge>
                      )}
                    </div>
                    {row.email && (
                      <p className="text-xs text-muted-foreground truncate">
                        {row.email}
                      </p>
                    )}
                  </div>
                  {canEdit && !row.isLeader && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        openRemovalDialog(
                          row.employeeId,
                          row.fullName,
                          row.taskCount,
                        )
                      }
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      aria-label={`Usuń ${row.fullName} z projektu`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {canEdit && (
            <div className="border-t border-border pt-4 space-y-2">
              <Label htmlFor="add-members">Dodaj członków</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 min-w-0">
                  <MultiSelect
                    id="add-members"
                    options={addableEmployees.map((e) => ({
                      value: e.id,
                      label: `${e.lastName} ${e.firstName}`,
                    }))}
                    selected={pendingAddIds}
                    onChange={setPendingAddIds}
                    placeholder={
                      employeesLoading
                        ? 'Ładowanie...'
                        : addableEmployees.length === 0
                        ? 'Brak dostępnych pracowników'
                        : 'Wybierz pracowników'
                    }
                    searchable
                    searchPlaceholder="Szukaj pracownika..."
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleAddMembers}
                  disabled={pendingAddIds.length === 0 || addingMember}
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  {addingMember
                    ? 'Dodawanie...'
                    : pendingAddIds.length > 1
                    ? `Dodaj (${pendingAddIds.length})`
                    : 'Dodaj'}
                </Button>
              </div>
            </div>
          )}

          {!canEdit && (
            <p className="text-xs text-muted-foreground">
              Brak uprawnień do edycji listy członków. Skontaktuj się z liderem
              projektu lub administratorem.
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!removalTarget}
        onOpenChange={(v) => {
          if (!v && !removing) setRemovalTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Usuń członka projektu
            </DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz usunąć użytkownika{' '}
              <strong>{removalTarget?.fullName}</strong> z projektu? Tej
              operacji nie można cofnąć.
            </DialogDescription>
          </DialogHeader>

          {removalTarget && removalTarget.taskCount > 0 ? (
            <div className="space-y-2">
              <p className="text-sm">
                Ta osoba jest przypisana do{' '}
                <strong>
                  {removalTarget.taskCount}{' '}
                  {removalTarget.taskCount === 1 ? 'zadania' : 'zadań'}
                </strong>{' '}
                w tym projekcie. Wybierz osobę, której zadania zostaną
                przepisane.
              </p>
              <Label htmlFor="reassign-to">Przepisz zadania na</Label>
              <Select
                value={reassignToId || 'none'}
                onValueChange={(v) =>
                  setReassignToId(v === 'none' ? '' : v)
                }
                disabled={removing}
              >
                <SelectTrigger id="reassign-to">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    Nie przepisuj — zadania pozostaną nieprzypisane
                  </SelectItem>
                  {reassignOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Ta osoba nie ma przypisanych zadań w projekcie.
            </p>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRemovalTarget(null)}
              disabled={removing}
            >
              Anuluj
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmRemove}
              disabled={removing}
            >
              {removing ? 'Usuwanie...' : 'Usuń'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
